"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.Block = void 0;
const printTree_1 = require("tree-dump/lib/printTree");
const hash_1 = require("../../../json-hash/hash");
const iterator_1 = require("../../../util/iterator");
const Inline_1 = require("./Inline");
const util_1 = require("../slice/util");
const Range_1 = require("../rga/Range");
const toLine_1 = require("pojo-dump/lib/toLine");
const OverlayPoint_1 = require("../overlay/OverlayPoint");
class Block extends Range_1.Range {
    constructor(txt, path, marker, start, end) {
        super(txt.str, start, end);
        this.txt = txt;
        this.path = path;
        this.marker = marker;
        this.start = start;
        this.end = end;
        this.parent = null;
        this.children = [];
        // ----------------------------------------------------------------- Stateful
        this.hash = 0;
    }
    /**
     * @returns Stable unique identifier within a list of blocks. Used for React
     * or other rendering library keys.
     */
    key() {
        if (!this.marker)
            return this.tag();
        const id = this.marker.id;
        return id.sid.toString(36) + id.time.toString(36);
    }
    tag() {
        return (0, util_1.getTag)(this.path);
    }
    attr() {
        const path = this.path;
        const length = path.length;
        if (length === 0)
            return;
        const tagStep = path[length - 1];
        if (!Array.isArray(tagStep))
            return;
        return tagStep[2];
    }
    isTop() {
        return !this.parent?.parent;
    }
    isLeaf() {
        return false;
    }
    /**
     * Iterate through all overlay points of this block, until the next marker
     * (regardless if that marker is a child or not).
     */
    points0(withMarker = false) {
        const txt = this.txt;
        const overlay = txt.overlay;
        const iterator = overlay.points0(this.marker);
        let closed = false;
        return () => {
            if (withMarker) {
                withMarker = false;
                return this.marker ?? overlay.START;
            }
            if (closed)
                return;
            const point = iterator();
            if (!point)
                return;
            if (point.isMarker()) {
                closed = true;
                return;
            }
            return point;
        };
    }
    points(withMarker) {
        return new iterator_1.UndEndIterator(this.points0(withMarker));
    }
    tuples0() {
        const overlay = this.txt.overlay;
        const marker = this.marker;
        const iterator = overlay.tuples0(marker);
        let closed = false;
        return () => {
            if (closed)
                return;
            let pair = iterator();
            while (!marker && pair && pair[1] && pair[1].cmpSpatial(this.start) < 0)
                pair = iterator();
            if (!pair)
                return (closed = true), void 0;
            const second = pair[1];
            if (!second || (second instanceof OverlayPoint_1.OverlayPoint && second.isMarker()))
                closed = true;
            return pair;
        };
    }
    /**
     * @todo Consider moving inline-related methods to {@link LeafBlock}.
     */
    texts0() {
        const txt = this.txt;
        const overlay = txt.overlay;
        const iterator = this.tuples0();
        const start = this.start;
        const end = this.end;
        const startIsMarker = overlay.isMarker(start.id);
        let isFirst = true;
        let next = iterator();
        let closed = false;
        const newIterator = () => {
            if (closed)
                return;
            const pair = next;
            next = iterator();
            if (!pair)
                return;
            const [overlayPoint1, overlayPoint2] = pair;
            let point1 = overlayPoint1;
            let point2 = overlayPoint2;
            if (isFirst) {
                isFirst = false;
                if (start.cmpSpatial(overlayPoint1) > 0)
                    point1 = start;
                if (startIsMarker) {
                    point1 = point1.clone();
                    point1.step(1);
                    if (point1.cmpSpatial(point2) >= 0)
                        return newIterator();
                }
            }
            // TODO: PERF: Speed up this check. Do the check only if necessary.
            if (end.cmpSpatial(overlayPoint2) < 0) {
                closed = true;
                point2 = end;
            }
            return new Inline_1.Inline(txt, overlayPoint1, overlayPoint2, point1, point2);
        };
        return newIterator;
    }
    /**
     * @todo Consider moving inline-related methods to {@link LeafBlock}.
     */
    texts() {
        return new iterator_1.UndEndIterator(this.texts0());
    }
    text() {
        let str = '';
        const children = this.children;
        const length = children.length;
        for (let i = 0; i < length; i++)
            str += children[i].text();
        return str;
    }
    // ------------------------------------------------------------------- export
    toJson() {
        const data = this.attr();
        const attr = data !== void 0 ? { data } : null;
        const node = [this.tag(), attr];
        const children = this.children;
        const length = children.length;
        for (let i = 0; i < length; i++)
            node.push(children[i].toJson());
        return node;
    }
    refresh() {
        const { path, children } = this;
        let state = hash_1.CONST.START_STATE;
        state = (0, hash_1.updateJson)(state, path);
        const marker = this.marker;
        if (marker) {
            state = (0, hash_1.updateNum)(state, marker.markers[0].refresh());
            state = (0, hash_1.updateNum)(state, marker.textHash);
        }
        else {
            state = (0, hash_1.updateNum)(state, this.txt.overlay.leadingTextHash);
        }
        for (let i = 0; i < children.length; i++)
            state = (0, hash_1.updateNum)(state, children[i].refresh());
        return (this.hash = state);
    }
    // ---------------------------------------------------------------- Printable
    toStringName() {
        return 'Block';
    }
    toStringHeader() {
        const hash = `#${this.hash.toString(36).slice(-4)}`;
        const tag = this.path.map((step) => (0, util_1.formatStep)(step)).join('.');
        const data = (0, toLine_1.toLine)(this.attr());
        const header = `${super.toString('', true)} ${hash} ${tag} ${data}`;
        return header;
    }
    toString(tab = '') {
        const header = this.toStringHeader();
        const hasChildren = !!this.children.length;
        return (header +
            (0, printTree_1.printTree)(tab, [
                this.marker ? (tab) => this.marker.toString(tab) : null,
                this.marker && hasChildren ? () => '' : null,
                hasChildren
                    ? (tab) => 'children' +
                        (0, printTree_1.printTree)(tab, this.children.map((child, i) => (tab) => `${i + 1}. ` + child.toString(tab + '  ' + ' '.repeat(String(i + 1).length))))
                    : null,
            ]));
    }
}
exports.Block = Block;
